import { ReactNode, useEffect, useRef, useCallback, useState, CSSProperties } from 'react';
import styles from './Carousel.module.scss';

export type CarouselAnimation = 'none' | 'fade' | 'slide';

export interface CarouselProps {
  /** Array of child elements to display as slides */
  children: ReactNode[];
  /** Index of the currently active slide (controlled) */
  selectedItem?: number;
  /** Callback fired when the active slide changes */
  onChange?: (index: number) => void;
  /** Whether to auto-advance slides */
  autoPlay?: boolean;
  /** Time in ms between auto-advances (default: 15000) */
  interval?: number;
  /** Whether to loop back to the first slide after the last */
  infiniteLoop?: boolean;
  /** Transition animation type (default: 'none') */
  animation?: CarouselAnimation;
  /** Transition duration in ms (default: 500) */
  animationDuration?: number;
}

type TransitionPhase = 'idle' | 'preparing' | 'animating';

const Carousel = ({
  children,
  selectedItem = 0,
  onChange,
  autoPlay = false,
  interval = 15000,
  infiniteLoop = false,
  animation = 'none',
  animationDuration = 500,
}: CarouselProps) => {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [phase, setPhase] = useState<TransitionPhase>('idle');
  const [activeIndex, setActiveIndex] = useState(selectedItem);
  const [exitingIndex, setExitingIndex] = useState<number | null>(null);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const rafRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (selectedItem === activeIndex) return;
    if (animation === 'none') {
      setActiveIndex(selectedItem);
      return;
    }

    // Phase 1: Position the entering slide at its start (no transition)
    const newDirection = selectedItem > activeIndex ? 'forward' : 'backward';
    setDirection(newDirection);
    setExitingIndex(activeIndex);
    setPhase('preparing');

    // Phase 2: On next frame, enable transitions and move to final positions
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        setActiveIndex(selectedItem);
        setPhase('animating');

        // Phase 3: Clean up after animation completes
        timeoutRef.current = setTimeout(() => {
          setExitingIndex(null);
          setPhase('idle');
        }, animationDuration);
      });
    });

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [selectedItem, activeIndex, animation, animationDuration]);

  const advance = useCallback(() => {
    if (!onChange || !children.length) return;

    const nextIndex = selectedItem + 1;
    if (nextIndex >= children.length) {
      if (infiniteLoop) {
        onChange(0);
      }
    } else {
      onChange(nextIndex);
    }
  }, [selectedItem, children.length, infiniteLoop, onChange]);

  useEffect(() => {
    if (autoPlay && children.length > 1) {
      timerRef.current = setInterval(advance, interval);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [autoPlay, interval, advance, children.length]);

  const getSlideClassName = (index: number): string => {
    const isActive = index === activeIndex;
    const isExiting = index === exitingIndex;
    const isEntering = index === selectedItem && phase === 'preparing';

    switch (animation) {
      case 'fade':
        return isActive ? styles.fadeSlideVisible : '';
      case 'slide': {
        // During 'preparing' phase: entering slide snaps to start position (no transition)
        if (isEntering) {
          return `${styles.slideNoTransition} ${direction === 'forward' ? styles.slideLeftIn : styles.slideRightIn}`;
        }
        // Active slide (after animating starts)
        if (isActive) return styles.slideVisible;
        // Exiting slide animates out
        if (isExiting) return direction === 'forward' ? styles.slideLeftOut : styles.slideRightOut;
        // All other inactive slides: hidden, no transition
        return styles.slideNoTransition;
      }
      default:
        return isActive ? `${styles.slide} ${styles.slideActive}` : styles.slide;
    }
  };

  const getContainerClassName = (): string => {
    switch (animation) {
      case 'fade':
        return `${styles.carousel} ${styles.fadeCarousel}`;
      case 'slide':
        return `${styles.carousel} ${styles.slideCarousel}`;
      default:
        return styles.carousel;
    }
  };

  const cssVars =
    animation !== 'none' ? ({ '--carousel-duration': `${animationDuration}ms` } as CSSProperties) : undefined;

  return (
    <div className={getContainerClassName()} style={cssVars}>
      {children.map((child, index) => (
        <div
          key={index}
          className={getSlideClassName(index)}
          aria-hidden={index !== activeIndex}
          data-testid={`leaderboard-presenter__carousel-${index}`}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

export default Carousel;
